import { useCallback, useEffect, useRef, useState } from 'react'
import type { DateValue } from '@mantine/dates'
import { showNotification } from '@mantine/notifications'
import { IconCircleOff } from '@tabler/icons-react'
import { getReferenceString } from '@medplum/core'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { useClient } from '@/hooks/useClient'
import { logError, logWarning, getErrorMessage } from '@/utils/errorUtils'

type StartResponse = Awaited<ReturnType<ReturnType<typeof useClient>['assistantStart']>>
type ListResponse = Awaited<ReturnType<ReturnType<typeof useClient>['assistantList']>>
type ConfigResponse = Awaited<ReturnType<ReturnType<typeof useClient>['assistantConfig']>>
export type AssistantConversationResponse = StartResponse
export type AssistantConversation = StartResponse['conversation']
export type ChatEntry = StartResponse['conversation']['entries'][number]
export type ConversationSummary = ListResponse['conversations'][number]
export type AssistantProviderOption = NonNullable<ConfigResponse['providers']>[number]
export type AssistantProviderName = AssistantProviderOption['provider']
export type AssistantDebugSelection = {
  provider: AssistantProviderName | null
  model: string | null
}
export type HiddenConversationDetailsEntry = {
  id: '__conversation-details'
  kind: 'hidden'
  provider: AssistantProviderName | null
  model: string | null
  maxTokens?: number | null
  forcedCurrentDate?: string | null
  timezone?: string | null
  conversationKey?: string | null
  // Conversation-level token usage, summed across the visible turns.
  usage?: AssistantUsage
}
export type ToolCallResultEntry = {
  id: string
  kind: 'tool_call_result'
  toolUseId: string
  name: string
  input: unknown
  output?: string
  isError?: boolean
  // The tool result's artifacts (answer/charts/tables/kpis), if any. Surfaced
  // behind a per-bubble "render artifacts" toggle so a tool's pre-built visuals
  // can be inspected the same way the assistant renders them.
  artifacts?: AssistantAnswer['artifacts']
}

// The terminal assistant answer carried on a turn entry.
export type AssistantAnswer = NonNullable<Extract<ChatEntry, { kind: 'turn' }>['assistant']>

// Token usage recorded on a turn (debug-only on the wire). Also the shape of the
// rolled-up conversation total the UI sums from the visible turns.
export type AssistantUsage = NonNullable<Extract<ChatEntry, { kind: 'turn' }>['usage']>

// Render units MessageList expands the grouped wire entries into: each turn
// becomes a user bubble, its processing steps (debug only), and an assistant
// bubble. DisplayEntry is the per-bubble shape MessageBubble switches on.
export type SystemDisplayEntry = { id: string; kind: 'system'; text: string }
export type UserDisplayEntry = { id: string; kind: 'user'; text: string }
export type AssistantDisplayEntry = {
  id: string
  kind: 'assistant'
  usage?: AssistantUsage
} & AssistantAnswer
export type ThinkingDisplayEntry = { id: string; kind: 'thinking'; text: string }
// A model response that failed validation and was retried (see
// ThreadProcessing's `retry` type) — `assistant` is the rejected attempt
// verbatim (its `error` explains why the model was asked to redo the turn).
export type RetryDisplayEntry = { id: string; kind: 'retry'; assistant: AssistantAnswer }
export type DisplayEntry =
  | HiddenConversationDetailsEntry
  | SystemDisplayEntry
  | UserDisplayEntry
  | AssistantDisplayEntry
  | ThinkingDisplayEntry
  | RetryDisplayEntry
  | ToolCallResultEntry

const DELETE_STREAMING_CONVERSATION_MESSAGE =
  'BeAssistant is still responding in that conversation. Wait for the response to finish, then try deleting it again.'
const STALE_CONVERSATION_MESSAGE =
  'This conversation was deleted in another tab. I started a new conversation.'
const CONVERSATION_UNAVAILABLE_MESSAGE = 'This conversation was deleted or is no longer available.'
const ASSISTANT_CONVERSATION_NOT_FOUND_REASON = 'assistant_conversation_not_found'

type Status = 'starting' | 'ready' | 'error'

export type AssistantLiveStatus = {
  // The live "thinking…" text rides in the in-flight turn's `processing` now;
  // MessageList reads it from there. Only the activity label and composing
  // phase come from the streaming envelope.
  activity?: string
  phase?: 'composing'
}

export type AssistantState = {
  status: Status
  entries: ChatEntry[]
  conversationId: string | null
  // Full path of the thread currently loaded/streamed/sent-to. The first
  // segment is the root conversation id; deeper segments name nested
  // sub-agent threads (debug mode only). `conversationId` is just its root.
  activeThreadPath: string[] | null
  conversationKey: string | null
  conversations: ConversationSummary[]
  debugTools: boolean
  maxTokens: number | null
  debugProviders: AssistantProviderOption[]
  currentDebugSelection: AssistantDebugSelection
  selectedDebugSelection: AssistantDebugSelection
  currentForcedTodayDate: string | null
  currentTimezone: string | null
  revealConversationDetails: boolean
  forceTodayDate: boolean
  forcedTodayDate: DateValue
  streaming: boolean
  liveStatus: AssistantLiveStatus
  startupError: string | null
  loadError: string | null
  // Debug-only superadmin impersonation: every actor with assistant threads
  // (own reference always included), the one currently viewed, the caller's
  // own reference (so the picker can mark it), and whether the picker should
  // render at all (debug tools on + superadmin).
  actors: string[]
  selectedActor: string | null
  selfActor: string | null
  showActorPicker: boolean
  setActor: (actor: string) => Promise<void>
  send: (text: string) => Promise<'sent' | 'already_streaming'>
  cancel: () => Promise<void>
  startNew: () => Promise<void>
  selectConversation: (threadPath: string[]) => Promise<void>
  refreshConversations: () => Promise<ConversationSummary[]>
  deleteConversation: (conversationId: string) => Promise<void>
  getCurrentConversation: () => Promise<AssistantConversationResponse | null>
  setDebugProvider: (provider: AssistantProviderName | null) => void
  setDebugModel: (model: string | null) => void
  setRevealConversationDetails: (value: boolean) => void
  setForceTodayDate: (value: boolean) => void
  setForcedTodayDate: (value: DateValue) => void
}

export function useAssistant(initialConversationId?: string): AssistantState {
  const client = useClient()
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const selfActor = profile ? (getReferenceString(profile) ?? null) : null

  const [entries, setEntries] = useState<ChatEntry[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [activeThreadPath, setActiveThreadPath] = useState<string[] | null>(null)
  const [conversationKey, setConversationKey] = useState<string | null>(null)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [debugTools, setDebugTools] = useState(false)
  const [maxTokens, setMaxTokens] = useState<number | null>(null)
  const [debugProviders, setDebugProviders] = useState<AssistantProviderOption[]>([])
  const [currentDebugSelection, setCurrentDebugSelection] = useState<AssistantDebugSelection>({
    provider: null,
    model: null,
  })
  const [currentForcedTodayDate, setCurrentForcedTodayDate] = useState<string | null>(null)
  const [currentTimezone, setCurrentTimezone] = useState<string | null>(null)
  const [selectedDebugSelection, setSelectedDebugSelection] = useState<AssistantDebugSelection>({
    provider: null,
    model: null,
  })
  const [revealConversationDetails, setRevealConversationDetails] = useState(false)
  const [forceTodayDate, setForceTodayDateState] = useState(false)
  const [forcedTodayDate, setForcedTodayDateState] = useState<DateValue>(
    () => formatDateForAssistantOverride(new Date()) ?? null,
  )
  const [actors, setActors] = useState<string[]>([])
  // null = the user's own threads; a string = an impersonated actor (debug-only
  // superadmin affordance). The wire `actor` param is only sent for the latter.
  const [selectedActorOverride, setSelectedActorOverride] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>('starting')
  const [streaming, setStreaming] = useState(false)
  const [liveStatus, setLiveStatus] = useState<AssistantLiveStatus>({})
  const [startupError, setStartupError] = useState<string | null>(null)
  // Set when loading an existing conversation fails (e.g. the server can't
  // project the stored thread). Rendered in the chat area instead of letting a
  // generic exception surface as a toast.
  const [loadError, setLoadError] = useState<string | null>(null)

  // Synchronous mirrors of state. Async callbacks (poll loop, send) read and
  // write these directly so they never depend on React having flushed a
  // pending setState.
  const entriesRef = useRef<ChatEntry[]>([])
  const streamingRef = useRef(false)
  const debugProvidersRef = useRef<AssistantProviderOption[]>([])
  const selectedDebugSelectionRef = useRef<AssistantDebugSelection>({
    provider: null,
    model: null,
  })
  const debugToolsRef = useRef(false)
  const forceTodayDateRef = useRef(false)
  const forcedTodayDateRef = useRef<DateValue>(formatDateForAssistantOverride(new Date()) ?? null)

  // Synchronous mirrors of the actor override and own reference, so async
  // callbacks build the right wire params without waiting on a state flush.
  const selectedActorOverrideRef = useRef<string | null>(null)
  const selfActorRef = useRef<string | null>(selfActor)
  selfActorRef.current = selfActor

  // Synchronous mirror of the loaded thread path. Async callbacks race-guard
  // against it (bail if the user switched threads mid-flight) and build the
  // `threadPath` argument every assistant call now takes.
  const activeThreadPathRef = useRef<string[] | null>(null)
  const streamAbortRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)

  // The wire `actor` param for every assistant call: present only while
  // impersonating another actor (server refuses it outside debug/superadmin).
  const actorParam = useCallback((): { actor?: string } => {
    const override = selectedActorOverrideRef.current
    return override && override !== selfActorRef.current ? { actor: override } : {}
  }, [])

  const writeSelectedActorOverride = useCallback((next: string | null) => {
    selectedActorOverrideRef.current = next
    setSelectedActorOverride(next)
  }, [])

  const writeActiveThreadPath = useCallback((path: string[] | null) => {
    activeThreadPathRef.current = path
    setActiveThreadPath(path)
    setConversationId(path?.[0] ?? null)
  }, [])

  const writeEntries = useCallback((next: ChatEntry[]) => {
    entriesRef.current = next
    setEntries(next)
  }, [])

  const writeStreaming = useCallback((next: boolean) => {
    streamingRef.current = next
    setStreaming(next)
    // Live status is only meaningful while a turn is in flight. Clear it on
    // any transition out of streaming so we don't show a stale label after
    // the bubble has been replaced with the final answer.
    if (!next) setLiveStatus({})
  }, [])

  const readLiveStatus = useCallback(
    (streaming: { currentActivity?: string; currentPhase?: 'composing' }): AssistantLiveStatus => ({
      ...(streaming.currentActivity !== undefined && { activity: streaming.currentActivity }),
      ...(streaming.currentPhase !== undefined && { phase: streaming.currentPhase }),
    }),
    [],
  )

  const writeDebugProviders = useCallback((next: AssistantProviderOption[]) => {
    debugProvidersRef.current = next
    setDebugProviders(next)
  }, [])

  const writeSelectedDebugSelection = useCallback((next: AssistantDebugSelection) => {
    selectedDebugSelectionRef.current = next
    setSelectedDebugSelection(next)
  }, [])

  const writeDebugTools = useCallback((next: boolean) => {
    debugToolsRef.current = next
    setDebugTools(next)
  }, [])

  const writeForceTodayDate = useCallback((next: boolean) => {
    forceTodayDateRef.current = next
    setForceTodayDateState(next)
  }, [])

  const writeForcedTodayDate = useCallback((next: DateValue) => {
    forcedTodayDateRef.current = next
    setForcedTodayDateState(next)
  }, [])

  const writeCurrentConversationMeta = useCallback(
    (
      response: Pick<StartResponse, 'conversation' | 'key'> & {
        conversation: Pick<AssistantConversation, 'provider' | 'model'> & {
          forcedCurrentDate?: string
          timezone?: string
        }
      },
    ) => {
      const conversation = response.conversation
      const next = {
        provider: conversation.provider ?? null,
        model: conversation.model ?? null,
      }
      setCurrentDebugSelection(next)
      setCurrentForcedTodayDate(conversation.forcedCurrentDate ?? null)
      setCurrentTimezone(conversation.timezone ?? null)
      setConversationKey(response.key ?? null)
      writeSelectedDebugSelection(next)
    },
    [writeSelectedDebugSelection],
  )

  const stopStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort()
      streamAbortRef.current = null
    }
  }, [])

  // Open an SSE stream for an in-flight assistant turn and apply each event
  // to local state. Mirrors the previous polling loop's merge/live-status
  // logic; differences are (1) entries arrive as deltas event-by-event,
  // (2) the stream closes itself when streaming flips false, and (3) the
  // connection is canceled via AbortController on unmount / conv-switch.
  //
  // On error the stream attempts ONE reconnect with the latest sinceId so a
  // dev-mode HMR rebuild or transient network blip doesn't strand the UI in
  // streaming=true.
  const startStream = useCallback(
    (threadPath: string[], opts: { initialSinceId?: string } = {}) => {
      stopStream()
      const controller = new AbortController()
      streamAbortRef.current = controller
      const key = pathKey(threadPath)

      const run = async (sinceId: string | undefined, isRetry: boolean): Promise<void> => {
        try {
          const iter = await client.assistantStream(
            { threadPath, ...(sinceId !== undefined && { sinceId }), ...actorParam() },
            { signal: controller.signal },
          )
          for await (const ev of iter) {
            if (!isMountedRef.current) return
            if (pathKey(activeThreadPathRef.current) !== key) return
            if (controller.signal.aborted) return
            if (ev.entries.length > 0) {
              writeEntries(mergeEntries(entriesRef.current, ev.entries))
            }
            if (ev.streaming) setLiveStatus(readLiveStatus(ev.streaming))
            writeStreaming(ev.streaming !== undefined)
            if (!ev.streaming) return
          }
        } catch (err) {
          if (controller.signal.aborted) return
          if (!isMountedRef.current) return
          if (pathKey(activeThreadPathRef.current) !== key) return
          if (!isRetry) {
            logWarning('assistant', 'stream error, reconnecting', getErrorMessage(err))
            const latest = entriesRef.current[entriesRef.current.length - 1]?.id
            await run(latest, true)
            return
          }
          logError('assistant', 'stream failed after retry', err)
          writeStreaming(false)
        }
      }

      void run(opts.initialSinceId, false)
    },
    [actorParam, client, readLiveStatus, stopStream, writeEntries, writeStreaming],
  )

  const refreshConversations = useCallback(async (): Promise<ConversationSummary[]> => {
    try {
      const res = await client.assistantList({ ...actorParam() })
      if (!isMountedRef.current) return res.conversations
      setConversations(res.conversations)
      return res.conversations
    } catch (err) {
      logError('assistant', 'failed to list conversations', err)
      return []
    }
  }, [actorParam, client])

  const loadAssistantConfig = useCallback(async (): Promise<void> => {
    try {
      const res = await client.assistantConfig({})
      if (!isMountedRef.current) return
      writeDebugTools(res.debugTools)
      writeDebugProviders(res.providers ?? [])
      setMaxTokens(res.maxTokens ?? null)
    } catch (err) {
      logError('assistant', 'failed to load assistant config', err)
      if (!isMountedRef.current) return
      writeDebugTools(false)
      writeDebugProviders([])
      setMaxTokens(null)
    }
  }, [client, writeDebugProviders, writeDebugTools])

  // Populate the debug actor picker. Only meaningful for a superadmin with
  // debug tools on — anyone else keeps an empty list (and the server would
  // refuse the call anyway). Own reference is merged in so the picker always
  // offers a way back to "yourself", even with no threads yet.
  const loadActors = useCallback(async (): Promise<void> => {
    if (!debugToolsRef.current || !medplum.isSuperAdmin()) {
      setActors([])
      return
    }
    try {
      const res = await client.assistantActors({})
      if (!isMountedRef.current) return
      const self = selfActorRef.current
      setActors([...new Set([...(self ? [self] : []), ...res.actors])])
    } catch (err) {
      logError('assistant', 'failed to list assistant actors', err)
      if (isMountedRef.current) setActors([])
    }
  }, [client, medplum])

  const startNewConversation = useCallback(
    async (selection?: AssistantDebugSelection): Promise<void> => {
      stopStream()
      setLoadError(null)
      const forcedCurrentDate =
        debugToolsRef.current && forceTodayDateRef.current
          ? formatDateForAssistantOverride(forcedTodayDateRef.current)
          : undefined
      // The caller's browser timezone; the assistant renders all instant dates in
      // it (the model is timezone-agnostic — tools convert). See docs/ASSISTANT_TIMEZONE.md.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const input = {
        ...(selection?.provider && selection.model
          ? { provider: selection.provider, model: selection.model }
          : {}),
        ...(forcedCurrentDate ? { forcedCurrentDate } : {}),
        ...(timezone ? { timezone } : {}),
        ...actorParam(),
      }
      const res = await client.assistantStart(input)
      const conversation = res.conversation
      if (!isMountedRef.current) return
      writeActiveThreadPath([conversation.threadId])
      writeEntries(conversation.entries)
      const isStreaming = conversation.streaming !== undefined
      writeStreaming(isStreaming)
      if (conversation.streaming) setLiveStatus(readLiveStatus(conversation.streaming))
      writeCurrentConversationMeta(res)
      if (isStreaming) {
        const lastId = conversation.entries[conversation.entries.length - 1]?.id
        startStream([conversation.threadId], { initialSinceId: lastId })
      }
      void refreshConversations()
    },
    [
      actorParam,
      client,
      readLiveStatus,
      refreshConversations,
      startStream,
      stopStream,
      writeActiveThreadPath,
      writeCurrentConversationMeta,
      writeEntries,
      writeStreaming,
    ],
  )

  const loadConversation = useCallback(
    async (threadPath: string[]): Promise<void> => {
      stopStream()
      const key = pathKey(threadPath)
      writeActiveThreadPath(threadPath)
      writeEntries([])
      writeStreaming(false)
      setLoadError(null)
      let res: AssistantConversationResponse
      try {
        res = await client.assistantGet({ threadPath, ...actorParam() })
      } catch (err) {
        logError('assistant', 'failed to load conversation', err)
        if (!isMountedRef.current) return
        // Only surface the error if the user is still on this thread.
        if (pathKey(activeThreadPathRef.current) !== key) return
        setLoadError('This conversation failed to load.')
        return
      }
      const conversation = res.conversation
      if (!isMountedRef.current) return
      // Bail if user switched again before this resolved.
      if (pathKey(activeThreadPathRef.current) !== key) return
      writeEntries(conversation.entries)
      const isStreaming = conversation.streaming !== undefined
      writeStreaming(isStreaming)
      if (conversation.streaming) setLiveStatus(readLiveStatus(conversation.streaming))
      writeCurrentConversationMeta(res)
      if (isStreaming) {
        const lastId = conversation.entries[conversation.entries.length - 1]?.id
        startStream(threadPath, { initialSinceId: lastId })
      }
    },
    [
      actorParam,
      client,
      readLiveStatus,
      startStream,
      stopStream,
      writeActiveThreadPath,
      writeCurrentConversationMeta,
      writeEntries,
      writeStreaming,
    ],
  )

  // Boot: list conversations, jump into the newest, or start a new one.
  useEffect(() => {
    isMountedRef.current = true
    let cancelled = false
    void (async () => {
      try {
        await loadAssistantConfig()
        if (cancelled) return
        void loadActors()
        const list = await refreshConversations()
        if (cancelled) return
        if (initialConversationId) {
          // Deep link (?conversation=<id>) — e.g. the in-call rail's chat
          // history button. An unknown/expired id does NOT error: assistantGet
          // fabricates an empty conversation, so the page shows the empty-chat
          // state and the first send recovers via the stale-conversation path.
          await loadConversation([initialConversationId])
        } else if (shouldLoadInitialConversation(list[0])) {
          await loadConversation([list[0].conversationId])
        } else {
          await startNewConversation()
        }
        if (!cancelled) setStatus('ready')
      } catch (err) {
        if (cancelled) return
        logError('assistant', 'failed to start conversation', err)
        setStartupError(getErrorMessage(err))
        setStatus('error')
      }
    })()

    return () => {
      cancelled = true
      isMountedRef.current = false
      stopStream()
    }
  }, [
    initialConversationId,
    loadActors,
    loadAssistantConfig,
    loadConversation,
    refreshConversations,
    startNewConversation,
    stopStream,
  ])

  const send = useCallback(
    async (text: string): Promise<'sent' | 'already_streaming'> => {
      const trimmed = text.trim()
      const threadPath = activeThreadPathRef.current
      if (!trimmed || !threadPath) return 'sent'
      if (streamingRef.current) return 'sent'

      writeStreaming(true)
      try {
        const res = await client.assistantSend({ threadPath, text: trimmed, ...actorParam() })
        if (res.status === 'already_streaming') {
          // Another tab/window is already driving a turn on this conversation.
          // The typed text was NOT persisted server-side, so we skip appending
          // a local user entry and just attach to the in-flight stream. The
          // stream handler replays full state on connect (no sinceId), so the
          // other tab's user message and the in-flight assistant entry show
          // up in this view via the normal merge path. Caller (InputBar)
          // preserves the typed text so the user can re-send when the turn
          // finishes.
          startStream(threadPath)
          showNotification({
            color: 'yellow',
            title: 'BeAssistant',
            message:
              "A response is already in progress for this conversation — showing it now. Your message wasn't sent.",
          })
          return 'already_streaming'
        }
        writeEntries([
          ...entriesRef.current,
          {
            id: res.turnId,
            kind: 'turn',
            user: { text: trimmed },
            createdAt: new Date().toISOString(),
          },
        ])
        startStream(threadPath, { initialSinceId: res.turnId })
        // First user message promotes this conversation from "empty" to
        // "active" — refresh so the dropdown trigger picks up its start
        // time instead of falling back to "New chat".
        void refreshConversations()
        return 'sent'
      } catch (err) {
        writeStreaming(false)
        if (isUnavailableConversationError(err)) {
          logWarning('assistant', 'stale conversation send ignored', getErrorMessage(err))
          try {
            await startNewConversation(selectedDebugSelectionRef.current)
            showNotification({
              color: 'yellow',
              title: 'Conversation unavailable',
              message: STALE_CONVERSATION_MESSAGE,
            })
          } catch (startErr) {
            logError('assistant', 'failed to recover from stale conversation', startErr)
            showNotification({
              color: 'red',
              icon: <IconCircleOff />,
              title: 'BeAssistant',
              message: getErrorMessage(startErr),
            })
          }
          // 'already_streaming' so the composer keeps the typed text — the
          // recovery opened a FRESH conversation and the message was never
          // persisted; the user just re-sends it there.
          return 'already_streaming'
        }

        logError('assistant', 'failed to send message', err)
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'BeAssistant',
          message: getErrorMessage(err),
        })
        return 'sent'
      }
    },
    [
      actorParam,
      client,
      refreshConversations,
      startNewConversation,
      startStream,
      writeEntries,
      writeStreaming,
    ],
  )

  const startNew = useCallback(async () => {
    try {
      await startNewConversation(selectedDebugSelectionRef.current)
    } catch (err) {
      logError('assistant', 'failed to start new conversation', err)
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'BeAssistant',
        message: getErrorMessage(err),
      })
    }
  }, [startNewConversation])

  const cancel = useCallback(async () => {
    const threadPath = activeThreadPathRef.current
    if (!threadPath || !streamingRef.current) return
    try {
      await client.assistantCancel({ threadPath, ...actorParam() })
      // Don't flip `streaming` locally — the SSE terminal frame written by
      // the cancel endpoint will. Idempotent server-side; if the stream had
      // already closed, the next conversation read converges.
    } catch (err) {
      logError('assistant', 'failed to cancel conversation', err)
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'BeAssistant',
        message: getErrorMessage(err),
      })
    }
  }, [actorParam, client])

  const setDebugProvider = useCallback(
    (provider: AssistantProviderName | null) => {
      if (!provider) {
        writeSelectedDebugSelection({ provider: null, model: null })
        return
      }
      const models =
        debugProvidersRef.current.find((item) => item.provider === provider)?.models ?? []
      const currentModel = selectedDebugSelectionRef.current.model
      const model =
        currentModel && models.includes(currentModel) ? currentModel : (models[0] ?? null)
      writeSelectedDebugSelection({ provider, model })
    },
    [writeSelectedDebugSelection],
  )

  const setDebugModel = useCallback(
    (model: string | null) => {
      writeSelectedDebugSelection({
        provider: selectedDebugSelectionRef.current.provider,
        model,
      })
    },
    [writeSelectedDebugSelection],
  )

  const selectConversation = useCallback(
    async (threadPath: string[]) => {
      if (pathKey(threadPath) === pathKey(activeThreadPathRef.current)) return
      try {
        await loadConversation(threadPath)
      } catch (err) {
        logError('assistant', 'failed to load conversation', err)
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'BeAssistant',
          message: getErrorMessage(err),
        })
      }
    },
    [loadConversation],
  )

  // Switch the whole assistant view to another actor's conversations (debug-only
  // superadmin affordance). Unlike boot, the newest conversation loads regardless
  // of its age — the point is inspecting existing threads. With none, a fresh
  // conversation is started under the picked actor (fully interactive).
  const setActor = useCallback(
    async (actor: string) => {
      const next = actor === selfActorRef.current ? null : actor
      if (next === selectedActorOverrideRef.current) return
      stopStream()
      writeSelectedActorOverride(next)
      writeEntries([])
      writeStreaming(false)
      setLoadError(null)
      try {
        const list = await refreshConversations()
        if (!isMountedRef.current) return
        // Bail if the user picked yet another actor before this resolved.
        if (selectedActorOverrideRef.current !== next) return
        if (list[0]) {
          await loadConversation([list[0].conversationId])
        } else {
          await startNewConversation(selectedDebugSelectionRef.current)
        }
      } catch (err) {
        logError('assistant', 'failed to switch assistant actor', err)
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'BeAssistant',
          message: getErrorMessage(err),
        })
      }
    },
    [
      loadConversation,
      refreshConversations,
      startNewConversation,
      stopStream,
      writeEntries,
      writeSelectedActorOverride,
      writeStreaming,
    ],
  )

  const deleteConversation = useCallback(
    async (id: string) => {
      try {
        const result = await client.assistantDelete({ convId: id, ...actorParam() })
        if (result.blockedReason === 'streaming') {
          showNotification({
            color: 'yellow',
            title: 'Conversation is still active',
            message: DELETE_STREAMING_CONVERSATION_MESSAGE,
          })
          return
        }

        const list = (await refreshConversations()).filter(
          (conversation) => conversation.conversationId !== id,
        )
        showNotification({
          color: 'green',
          title: 'Conversation deleted',
          message: 'The conversation was permanently deleted.',
        })

        // Deleting a root cascades its sub-threads server-side, so navigate
        // away whenever the loaded thread lives under the deleted root.
        if (id !== activeThreadPathRef.current?.[0]) return

        const next = list[0]
        if (next) {
          await loadConversation([next.conversationId])
        } else {
          await startNewConversation(selectedDebugSelectionRef.current)
        }
      } catch (err) {
        logError('assistant', 'failed to delete conversation', err)
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'BeAssistant',
          message: getDeleteConversationErrorMessage(err),
        })
      }
    },
    [actorParam, client, loadConversation, refreshConversations, startNewConversation],
  )

  const getCurrentConversation =
    useCallback(async (): Promise<AssistantConversationResponse | null> => {
      const threadPath = activeThreadPathRef.current
      if (!threadPath) return null

      try {
        return await client.assistantGet({ threadPath, ...actorParam() })
      } catch (err) {
        logError('assistant', 'failed to download conversation', err)
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'BeAssistant',
          message: getErrorMessage(err),
        })
        return null
      }
    }, [actorParam, client])

  return {
    status,
    entries,
    conversationId,
    activeThreadPath,
    conversationKey,
    conversations,
    debugTools,
    maxTokens,
    debugProviders,
    currentDebugSelection,
    selectedDebugSelection,
    currentForcedTodayDate,
    currentTimezone,
    revealConversationDetails,
    forceTodayDate,
    forcedTodayDate,
    streaming,
    liveStatus,
    startupError,
    loadError,
    actors,
    selectedActor: selectedActorOverride ?? selfActor,
    selfActor,
    showActorPicker: debugTools && medplum.isSuperAdmin(),
    setActor,
    send,
    cancel,
    startNew,
    selectConversation,
    refreshConversations,
    deleteConversation,
    getCurrentConversation,
    setDebugProvider,
    setDebugModel,
    setRevealConversationDetails,
    setForceTodayDate: writeForceTodayDate,
    setForcedTodayDate: writeForcedTodayDate,
  }
}

// Stable identity for a thread path. Thread ids never contain ':' (it's the
// key separator server-side), so joining is collision-free. A null path keys
// to a sentinel that can't equal any real path.
function pathKey(path: string[] | null): string {
  return path === null ? ' none' : path.join(':')
}

function formatDateForAssistantOverride(value: DateValue): string | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined
  if (Number.isNaN(value.getTime())) return undefined
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function mergeEntries(prev: ChatEntry[], incoming: ChatEntry[]): ChatEntry[] {
  if (incoming.length === 0) return prev
  const byId = new Map<string, number>()
  prev.forEach((entry, idx) => byId.set(entry.id, idx))

  const next = [...prev]
  for (const entry of incoming) {
    const existingIdx = byId.get(entry.id)
    if (existingIdx !== undefined) {
      next[existingIdx] = entry
      continue
    }
    next.push(entry)
    byId.set(entry.id, next.length - 1)
  }
  return next
}

export function shouldLoadInitialConversation(
  conversation: ConversationSummary | undefined,
  now = new Date(),
): conversation is ConversationSummary {
  if (!conversation) return false

  const createdAt = new Date(conversation.createdAt)
  if (Number.isNaN(createdAt.getTime())) return false

  return (
    createdAt.getFullYear() === now.getFullYear() &&
    createdAt.getMonth() === now.getMonth() &&
    createdAt.getDate() === now.getDate()
  )
}

function getDeleteConversationErrorMessage(err: unknown): string {
  const message = getErrorMessage(err)
  if (
    message === 'Internal server error' ||
    message.includes('response is in progress') ||
    message.includes('streaming')
  ) {
    return DELETE_STREAMING_CONVERSATION_MESSAGE
  }
  return message
}

export function isUnavailableConversationError(err: unknown): boolean {
  if (getErrorMessage(err) === CONVERSATION_UNAVAILABLE_MESSAGE) return true
  if (typeof err !== 'object' || err === null) return false

  const data = (err as Record<string, unknown>).data
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).reason === ASSISTANT_CONVERSATION_NOT_FOUND_REASON
  )
}
